/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Check, ArrowRight, ArrowLeft, UploadCloud, Sparkles, Compass, FileImage, X as XIcon, FileText, Loader2, RefreshCw } from "lucide-react";
import { wizardSteps } from "@/lib/mockData";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

const NO_STRATEGY = "__keine__";
const MOOD_NONE = "__none__";
const MOOD_OPTIONS = [
  { value: "ruhig", label: "Ruhig" },
  { value: "fokussiert", label: "Fokussiert" },
  { value: "unsicher", label: "Unsicher" },
  { value: "gestresst", label: "Gestresst" },
  { value: "euphorisch", label: "Euphorisch" },
  { value: "fomo", label: "FOMO" },
  { value: "rache", label: "Rache-Impuls" },
  { value: "hektisch", label: "Hektisch" },
];
const TRADE_TYPE_LABEL = {
  scalp: "Scalp",
  daytrade: "Daytrade",
  swingtrade: "Swingtrade",
  hodl: "Hodl",
};
const moodLabel = (v) => MOOD_OPTIONS.find((m) => m.value === v)?.label || null;

const empty = {
  symbol: "", side: "long", leverage: "", market: "Aktien",
  entry: "", exit: "", size: "", opened: "", closed: "",
  stop: "", tp1: "", tp2: "", tp3: "",
  tp1_pct: "", tp2_pct: "", tp3_pct: "",
  risk: "1.0", fees: "", strategy: NO_STRATEGY,
  no_strategy_reason: "",
  mood_before: "",
  reason: "", tags: "", attachments: [],
  reflection: "", mood: "",
};

export default function TradeWizard({ open, onOpenChange }) {
  const [step, setStep] = useState(0);
  const [data, setData] = useState(empty);
  const [strategies, setStrategies] = useState([]);
  const total = wizardSteps.length;
  const progress = ((step + 1) / total) * 100;

  useEffect(() => {
    if (!open) return;
    axios
      .get(`${API}/api/strategies`, { withCredentials: true })
      .then((res) => {
        // Nur aktive Strategien sind im Trade-Wizard auswählbar
        const active = (res.data || []).filter((s) => s.status === "aktiv");
        setStrategies(active);
      })
      .catch(() => setStrategies([]));
  }, [open]);

  const activeStrategy = useMemo(
    () => strategies.find((s) => s.name === data.strategy) || null,
    [strategies, data.strategy]
  );

  const set = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const next = () => setStep((s) => Math.min(total - 1, s + 1));
  const prev = () => setStep((s) => Math.max(0, s - 1));

  const save = async () => {
    try {
      const stratId = activeStrategy?.id || null;
      const stratName =
        data.strategy === NO_STRATEGY ? "" : (activeStrategy?.name || data.strategy || "");
      const toNum = (v) => {
        if (v === "" || v === null || v === undefined) return null;
        const n = parseFloat(String(v).replace(",", "."));
        return Number.isFinite(n) ? n : null;
      };
      const payload = {
        asset: data.symbol.trim(),
        side: data.side,
        market: data.market,
        entry: toNum(data.entry),
        exit: toNum(data.exit),
        size: toNum(data.size),
        opened_at: data.opened || null,
        closed_at: data.closed || null,
        stop: toNum(data.stop),
        tp1: toNum(data.tp1),
        tp2: toNum(data.tp2),
        tp3: toNum(data.tp3),
        tp1_pct: toNum(data.tp1_pct),
        tp2_pct: toNum(data.tp2_pct),
        tp3_pct: toNum(data.tp3_pct),
        risk_pct: toNum(data.risk),
        leverage: toNum(data.leverage),
        fees: toNum(data.fees) ?? 0,
        strategy_id: stratId,
        strategy_name: stratName,
        no_strategy_reason: data.no_strategy_reason || "",
        mood_before: data.mood_before || "",
        mood_after: data.mood || "",
        reason: data.reason || "",
        reflection: data.reflection || "",
        tags: data.tags
          ? data.tags.split(",").map((t) => t.trim()).filter(Boolean)
          : [],
        attachments: data.attachments || [],
        status: data.exit ? "geschlossen" : "offen",
      };
      if (!payload.asset) {
        toast.error("Bitte trage mindestens ein Asset ein.");
        return;
      }
      await axios.post(`${API}/api/trades`, payload, { withCredentials: true });
      toast.success("Trade gespeichert", {
        description: "Als manueller Eintrag angelegt.",
      });
      setStep(0);
      setData(empty);
      onOpenChange(false);
    } catch (e) {
      toast.error("Speichern fehlgeschlagen", {
        description: e?.response?.data?.detail || "Bitte prüfe deine Eingaben.",
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-4xl p-0 max-h-[92vh] flex flex-col overflow-hidden"
        data-testid="trade-wizard"
      >
        <div className="grid md:grid-cols-[220px_1fr] flex-1 min-h-0">
          {/* Stepper */}
          <aside className="hidden md:flex flex-col gap-1 p-6 bg-secondary/50 border-r border-border overflow-y-auto">
            <div className="overline mb-3">Trade erfassen</div>
            <ol className="space-y-1">
              {wizardSteps.map((s, i) => {
                const isDone = i < step;
                const isActive = i === step;
                return (
                  <li key={s.key}>
                    <button
                      onClick={() => setStep(i)}
                      data-testid={`wizard-step-${s.key}`}
                      className={`w-full text-left flex items-center gap-3 px-2.5 py-2 rounded-md text-sm transition-colors ${
                        isActive ? "bg-background border border-border" : "hover:bg-background/70"
                      }`}
                    >
                      <span
                        className={`grid place-items-center h-6 w-6 rounded-full text-[11px] font-mono border ${
                          isDone
                            ? "bg-primary text-primary-foreground border-primary"
                            : isActive
                            ? "border-primary text-primary"
                            : "border-border text-muted-foreground"
                        }`}
                      >
                        {isDone ? <Check className="h-3 w-3" strokeWidth={2} /> : i + 1}
                      </span>
                      <span className={isActive ? "text-foreground" : "text-muted-foreground"}>
                        {s.label}
                      </span>
                    </button>
                  </li>
                );
              })}
            </ol>
          </aside>

          {/* Content */}
          <div className="flex flex-col min-h-0">
            <div className="px-6 md:px-8 pt-6 md:pt-8 pb-2 shrink-0">
              <DialogHeader className="mb-2">
                <DialogTitle className="font-heading text-2xl">
                  {wizardSteps[step].label}
                </DialogTitle>
                <DialogDescription className="text-muted-foreground">
                  Schritt {step + 1} von {total}
                </DialogDescription>
              </DialogHeader>

              <Progress value={progress} className="h-1.5 mt-2" />
            </div>

            <div className="flex-1 overflow-y-auto px-6 md:px-8 py-4 min-h-0" data-testid="wizard-scroll">
              <div className="min-h-[260px]">
              {step === 0 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="wizard-general">
                  <Field label="Asset / Instrument" htmlFor="symbol">
                    <Input
                      id="symbol"
                      placeholder="z. B. SAP.DE"
                      value={data.symbol}
                      onChange={(e) => set("symbol", e.target.value)}
                      data-testid="wizard-symbol"
                    />
                  </Field>
                  <Field label="Markt">
                    <Select value={data.market} onValueChange={(v) => set("market", v)}>
                      <SelectTrigger data-testid="wizard-market"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Aktien">Aktien</SelectItem>
                        <SelectItem value="Indizes">Indizes</SelectItem>
                        <SelectItem value="FX">FX</SelectItem>
                        <SelectItem value="Krypto">Krypto</SelectItem>
                      </SelectContent>
                    </Select>
                  </Field>
                  <Field label="Seite">
                    <RadioGroup value={data.side} onValueChange={(v) => set("side", v)} className="flex gap-4 pt-2">
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="long" id="side-long" /> Long
                      </label>
                      <label className="flex items-center gap-2 text-sm">
                        <RadioGroupItem value="short" id="side-short" /> Short
                      </label>
                    </RadioGroup>
                  </Field>
                  <Field label="Hebel">
                    <div className="flex items-center gap-2">
                      <Input
                        type="number" min="1" max="200" step="1"
                        inputMode="numeric"
                        placeholder="z. B. 10"
                        value={data.leverage}
                        onChange={(e) => set("leverage", e.target.value)}
                        data-testid="wizard-leverage"
                        className="w-28 font-mono"
                      />
                      <span className="text-xs text-muted-foreground">× (optional, 1 = kein Hebel)</span>
                    </div>
                    <div className="mt-2 flex flex-wrap gap-1.5">
                      {[1, 2, 5, 10, 20, 50, 100].map((v) => (
                        <button
                          key={v}
                          type="button"
                          onClick={() => set("leverage", String(v))}
                          className={`rounded-full border px-2.5 h-7 text-[11px] transition-colors ${
                            String(data.leverage) === String(v)
                              ? "border-primary/60 bg-primary/15 text-foreground"
                              : "border-border bg-secondary/40 text-muted-foreground hover:text-foreground"
                          }`}
                          data-testid={`wizard-leverage-preset-${v}`}
                        >
                          {v}×
                        </button>
                      ))}
                    </div>
                  </Field>
                  <Field label="Größe / Stückzahl">
                    <Input
                      placeholder="z. B. 40"
                      value={data.size}
                      onChange={(e) => set("size", e.target.value)}
                    />
                  </Field>
                </div>
              )}

              {step === 1 && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5" data-testid="wizard-entry-exit">
                  <Field label="Eröffnet am">
                    <Input type="datetime-local" value={data.opened} onChange={(e) => set("opened", e.target.value)} />
                  </Field>
                  <Field label="Geschlossen am">
                    <Input type="datetime-local" value={data.closed} onChange={(e) => set("closed", e.target.value)} />
                  </Field>
                  <Field label="Entry-Preis">
                    <Input value={data.entry} onChange={(e) => set("entry", e.target.value)} placeholder="0,00" />
                  </Field>
                  <Field label="Exit-Preis">
                    <Input value={data.exit} onChange={(e) => set("exit", e.target.value)} placeholder="0,00" />
                  </Field>
                </div>
              )}

              {step === 2 && (
                <div className="space-y-4" data-testid="wizard-risk">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <Field label="SL (Stop-Loss)">
                      <Input value={data.stop} onChange={(e) => set("stop", e.target.value)} placeholder="Preis" className="font-mono" />
                    </Field>
                    <Field label="TP1">
                      <Input value={data.tp1} onChange={(e) => set("tp1", e.target.value)} placeholder="Preis" className="font-mono" />
                    </Field>
                    <Field label="TP2">
                      <Input value={data.tp2} onChange={(e) => set("tp2", e.target.value)} placeholder="Preis" className="font-mono" />
                    </Field>
                    <Field label="TP3">
                      <Input value={data.tp3} onChange={(e) => set("tp3", e.target.value)} placeholder="Preis" className="font-mono" />
                    </Field>
                  </div>
                  <div className="rounded-lg border border-[hsl(38_92%_50%/0.35)] bg-[hsl(38_92%_50%/0.06)] p-3 flex items-start gap-2 text-xs">
                    <span className="text-[hsl(38_92%_66%)] font-bold shrink-0">!</span>
                    <div>
                      <div className="font-medium text-foreground">TP-Prozente beziehen sich auf die jeweils verbleibende Position</div>
                      <p className="text-muted-foreground mt-0.5 leading-relaxed">
                        TP1 50 % schließt 50 % der Ursprungs-Position. TP2 50 % nimmt dann 50 % vom Rest — also 25 % der ursprünglichen Position. Der offene Restanteil läuft weiter.
                      </p>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="text-[10px] uppercase tracking-wider text-muted-foreground flex items-end pb-2">Anteil je TP (% vom Rest)</div>
                    <Field label="TP1 % (vom Original)">
                      <Input value={data.tp1_pct} onChange={(e) => set("tp1_pct", e.target.value)} placeholder="z. B. 50" className="font-mono" />
                    </Field>
                    <Field label="TP2 % (vom Rest)">
                      <Input value={data.tp2_pct} onChange={(e) => set("tp2_pct", e.target.value)} placeholder="z. B. 50" className="font-mono" />
                    </Field>
                    <Field label="TP3 % (vom Rest)">
                      <Input value={data.tp3_pct} onChange={(e) => set("tp3_pct", e.target.value)} placeholder="z. B. 100" className="font-mono" />
                    </Field>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <Field label="Risiko in R (optional)">
                      <Input value={data.risk} onChange={(e) => set("risk", e.target.value)} placeholder="1.0" className="font-mono" />
                    </Field>
                    <Field label="Gebühren (€)">
                      <Input value={data.fees} onChange={(e) => set("fees", e.target.value)} placeholder="z. B. 1,50" data-testid="wizard-fees" className="font-mono" />
                    </Field>
                  </div>
                  <div className="rounded-xl border border-dashed border-border p-4 text-sm text-muted-foreground">
                    Broker-Daten überschreiben deine Eingaben nur nach Bestätigung. SL/TPs sind optional — sobald sie da sind, berechnen wir das R-Verhältnis automatisch.
                  </div>
                </div>
              )}

              {step === 3 && (
                <div className="space-y-5" data-testid="wizard-strategy">
                  <Field label="Verwendete Strategie">
                    <Select value={data.strategy} onValueChange={(v) => set("strategy", v)}>
                      <SelectTrigger data-testid="wizard-strategy-select"><SelectValue placeholder="Strategie wählen…" /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={NO_STRATEGY} data-testid="wizard-strategy-none">
                          Keine Strategie (z. B. FOMO, Impuls, News)
                        </SelectItem>
                        {strategies.length === 0 && (
                          <div className="px-2 py-2 text-xs text-muted-foreground italic">
                            Keine aktive Strategie vorhanden — leg eine unter „Strategien" an.
                          </div>
                        )}
                        {strategies.map((s) => (
                          <SelectItem key={s.id} value={s.name} data-testid={`wizard-strategy-option-${s.id}`}>
                            {s.name}
                            {s.trade_types?.length ? ` · ${s.trade_types.map((t) => TRADE_TYPE_LABEL[t] || t).join(", ")}` : ""}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {data.strategy === NO_STRATEGY && (
                      <p className="text-xs text-muted-foreground mt-2">
                        Ohne Strategie ist ok — halte den Grund fest, damit du in der Reflexion Muster erkennst.
                      </p>
                    )}
                  </Field>

                  {activeStrategy && (
                    <div
                      className="rounded-xl border border-primary/40 bg-primary/10 p-4 space-y-3"
                      data-testid="wizard-strategy-info"
                    >
                      <div className="flex items-center gap-2">
                        <Compass className="h-4 w-4 text-primary" strokeWidth={1.5} />
                        <div className="overline">Aus deiner Strategie</div>
                      </div>
                      <dl className="grid grid-cols-[100px_1fr] gap-x-3 gap-y-1.5 text-sm">
                        {activeStrategy.trade_types?.length > 0 && (
                          <>
                            <dt className="text-muted-foreground">Trade-Art</dt>
                            <dd>{activeStrategy.trade_types.map((t) => TRADE_TYPE_LABEL[t] || t).join(", ")}</dd>
                          </>
                        )}
                        {activeStrategy.rough_strategy && (
                          <>
                            <dt className="text-muted-foreground">Ansatz</dt>
                            <dd className="whitespace-pre-wrap">{activeStrategy.rough_strategy}</dd>
                          </>
                        )}
                        {activeStrategy.sl_description && (
                          <>
                            <dt className="text-muted-foreground">SL-Idee</dt>
                            <dd className="whitespace-pre-wrap">{activeStrategy.sl_description}</dd>
                          </>
                        )}
                        {activeStrategy.tp_description && (
                          <>
                            <dt className="text-muted-foreground">TP-Idee</dt>
                            <dd className="whitespace-pre-wrap">{activeStrategy.tp_description}</dd>
                          </>
                        )}
                      </dl>
                      <p className="text-xs text-muted-foreground italic">
                        Der Coach zieht diese Angaben später als Grundlage für seine Rückfragen heran.
                      </p>
                    </div>
                  )}

                  {data.strategy === NO_STRATEGY ? (
                    <Field label="Grund ohne Strategie">
                      <Textarea
                        rows={4}
                        placeholder="z. B. FOMO nach Kursanstieg, Impuls durch News, Rache-Trade nach Verlust…"
                        value={data.no_strategy_reason}
                        onChange={(e) => set("no_strategy_reason", e.target.value)}
                        data-testid="wizard-no-strategy-reason"
                      />
                    </Field>
                  ) : (
                    <Field label="Setup-Notiz (optional)">
                      <Textarea
                        rows={4}
                        placeholder="Warum passt dieser Trade zur Strategie? Wo weichst du bewusst ab?"
                        value={data.reason}
                        onChange={(e) => set("reason", e.target.value)}
                      />
                    </Field>
                  )}

                  <Field label="Stimmung beim Trade (optional)">
                    <Select
                      value={data.mood_before || MOOD_NONE}
                      onValueChange={(v) => set("mood_before", v === MOOD_NONE ? "" : v)}
                    >
                      <SelectTrigger className="w-64" data-testid="wizard-mood-before"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={MOOD_NONE} data-testid="wizard-mood-before-none">
                          Keine Angabe
                        </SelectItem>
                        {MOOD_OPTIONS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-muted-foreground mt-1.5">
                      Freiwillig. Hilft dir, Muster zwischen Gefühlszustand und Trade-Ergebnis zu erkennen.
                    </p>
                  </Field>
                </div>
              )}

              {step === 4 && (
                <div className="space-y-5" data-testid="wizard-journal">
                  <Field label="Journal-Eintrag (optional)">
                    <Textarea
                      rows={6}
                      placeholder="Gedanken, Beobachtungen, Emotionen…"
                      value={data.reflection}
                      onChange={(e) => set("reflection", e.target.value)}
                    />
                  </Field>
                  <Field label="Stimmung nach dem Trade (optional)">
                    <Select
                      value={data.mood || MOOD_NONE}
                      onValueChange={(v) => set("mood", v === MOOD_NONE ? "" : v)}
                    >
                      <SelectTrigger className="w-64" data-testid="wizard-mood-after"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value={MOOD_NONE} data-testid="wizard-mood-after-none">
                          Keine Angabe
                        </SelectItem>
                        {MOOD_OPTIONS.map((m) => (
                          <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>
              )}

              {step === 5 && (
                <AttachmentsField
                  attachments={data.attachments}
                  onChange={(next) => set("attachments", next)}
                />
              )}

              {step === 6 && (
                <WizardCoachPanel data={data} activeStrategy={activeStrategy} moodLabel={moodLabel} tradeTypeLabel={TRADE_TYPE_LABEL} noStrategyValue={NO_STRATEGY} />
              )}

              {step === 7 && (
                <div className="space-y-4" data-testid="wizard-review">
                  <div className="grid grid-cols-2 gap-4">
                    <ReviewCell label="Asset" value={data.symbol || "—"} />
                    <ReviewCell label="Seite" value={data.side} />
                    <ReviewCell label="Entry" value={data.entry || "—"} />
                    <ReviewCell label="Exit" value={data.exit || "—"} />
                    <ReviewCell label="Größe" value={data.size || "—"} />
                    <ReviewCell label="Risiko" value={`${data.risk} R`} />
                    <ReviewCell label="Gebühren" value={data.fees ? `${data.fees} €` : "—"} />
                    <ReviewCell
                      label="Strategie"
                      value={data.strategy === NO_STRATEGY ? "Keine Strategie" : data.strategy}
                    />
                    <ReviewCell label="Stimmung vorher" value={moodLabel(data.mood_before) || "—"} />
                    <ReviewCell label="Stimmung nachher" value={moodLabel(data.mood) || "—"} />
                  </div>
                  <div className="rounded-xl border border-border p-4 text-sm text-muted-foreground">
                    Wird als <span className="font-medium text-foreground">manueller Eintrag</span> gespeichert.
                    Broker-Sync kann Preise später ergänzen, sofern du zustimmst.
                  </div>
                </div>
              )}
              </div>
            </div>

            <div className="flex items-center justify-between px-6 md:px-8 py-4 border-t border-border shrink-0 bg-background">
              <Button
                variant="ghost"
                onClick={prev}
                disabled={step === 0}
                data-testid="wizard-prev"
              >
                <ArrowLeft className="mr-2 h-4 w-4" strokeWidth={1.5} /> Zurück
              </Button>
              {step < total - 1 ? (
                <Button onClick={next} className="rounded-full h-11 px-5" data-testid="wizard-next">
                  Weiter <ArrowRight className="ml-2 h-4 w-4" strokeWidth={1.5} />
                </Button>
              ) : (
                <Button onClick={save} className="rounded-full h-11 px-6" data-testid="wizard-save">
                  <Check className="mr-2 h-4 w-4" strokeWidth={1.5} /> Speichern
                </Button>
              )}
            </div>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, htmlFor, children }) {
  return (
    <div className="space-y-1.5">
      <Label htmlFor={htmlFor} className="text-xs uppercase tracking-wider text-muted-foreground">
        {label}
      </Label>
      {children}
    </div>
  );
}

const MAX_TOTAL_BYTES = 8 * 1024 * 1024; // 8 MB gesamt
const MAX_FILE_BYTES = 3 * 1024 * 1024;  // 3 MB pro Datei
const ACCEPT = "image/png,image/jpeg,image/webp,image/gif,application/pdf,text/plain";

const readAsDataUrl = (file) => new Promise((resolve, reject) => {
  const r = new FileReader();
  r.onload = () => resolve(r.result);
  r.onerror = reject;
  r.readAsDataURL(file);
});

function AttachmentsField({ attachments, onChange }) {
  const inputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  const [busy, setBusy] = useState(false);

  const currentBytes = (attachments || []).reduce((s, a) => s + (a.size || 0), 0);

  const addFiles = async (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    setBusy(true);
    try {
      let total = currentBytes;
      const next = [...attachments];
      for (const f of files) {
        if (f.size > MAX_FILE_BYTES) {
          toast.error(`„${f.name}" ist zu groß`, { description: "Maximal 3 MB pro Datei." });
          continue;
        }
        if (total + f.size > MAX_TOTAL_BYTES) {
          toast.error("Anhänge zu groß", { description: "Insgesamt max. 8 MB pro Trade." });
          break;
        }
        try {
          const dataUrl = await readAsDataUrl(f);
          next.push({
            id: `${Date.now()}_${Math.random().toString(36).slice(2, 8)}`,
            name: f.name || "unbenannt",
            type: f.type || "application/octet-stream",
            size: f.size,
            data_url: dataUrl,
          });
          total += f.size;
        } catch {
          toast.error(`„${f.name}" konnte nicht gelesen werden.`);
        }
      }
      onChange(next);
    } finally {
      setBusy(false);
      if (inputRef.current) inputRef.current.value = "";
    }
  };

  const openPicker = () => inputRef.current?.click();
  const remove = (id) => onChange(attachments.filter((a) => a.id !== id));

  const onDragOver = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(true); };
  const onDragLeave = (e) => { e.preventDefault(); e.stopPropagation(); setDragActive(false); };
  const onDrop = (e) => {
    e.preventDefault(); e.stopPropagation(); setDragActive(false);
    if (e.dataTransfer?.files?.length) addFiles(e.dataTransfer.files);
  };
  const onPaste = (e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const files = [];
    for (const it of items) {
      if (it.kind === "file") {
        const f = it.getAsFile();
        if (f) files.push(f);
      }
    }
    if (files.length) { e.preventDefault(); addFiles(files); }
  };

  const totalMB = (currentBytes / 1024 / 1024).toFixed(1);

  return (
    <div className="space-y-4" data-testid="wizard-attachments">
      <div
        onDragEnter={onDragOver}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
        onDrop={onDrop}
        onPaste={onPaste}
        onClick={openPicker}
        tabIndex={0}
        role="button"
        aria-label="Anhänge auswählen oder hierher ziehen"
        className={`rounded-xl border-2 border-dashed p-10 text-center cursor-pointer transition-colors focus:outline-none focus:ring-2 focus:ring-primary/40 ${
          dragActive ? "border-primary bg-primary/10" : "border-border hover:border-primary/50 hover:bg-secondary/40"
        }`}
        data-testid="wizard-attach-dropzone"
      >
        <UploadCloud className="mx-auto h-8 w-8 text-muted-foreground" strokeWidth={1.5} />
        <div className="mt-3 font-heading">Chart-Screenshots oder Notizen anhängen</div>
        <p className="text-sm text-muted-foreground mt-1">
          Datei hierher ziehen, einfügen (Strg+V) oder klicken zum Auswählen.
        </p>
        <p className="text-xs text-muted-foreground mt-2">
          Bilder, PDF oder TXT · max. 3 MB pro Datei · max. 8 MB gesamt
        </p>
        <Button
          type="button"
          variant="outline"
          className="rounded-full mt-4"
          onClick={(e) => { e.stopPropagation(); openPicker(); }}
          disabled={busy}
          data-testid="wizard-attach-btn"
        >
          {busy ? "Wird geladen…" : "Dateien auswählen"}
        </Button>
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={ACCEPT}
          className="hidden"
          onChange={(e) => addFiles(e.target.files)}
          data-testid="wizard-attach-input"
        />
      </div>

      {attachments.length > 0 && (
        <div className="rounded-xl border border-border bg-secondary/30 p-4">
          <div className="flex items-center justify-between mb-3">
            <div className="text-sm font-medium">{attachments.length} Anhang{attachments.length === 1 ? "" : "e"}</div>
            <div className="text-xs text-muted-foreground font-mono">{totalMB} MB / 8 MB</div>
          </div>
          <ul className="space-y-2" data-testid="wizard-attach-list">
            {attachments.map((a) => {
              const isImage = a.type?.startsWith("image/");
              const kb = a.size < 1024 * 1024 ? `${(a.size / 1024).toFixed(0)} KB` : `${(a.size / 1024 / 1024).toFixed(1)} MB`;
              return (
                <li
                  key={a.id}
                  className="flex items-center gap-3 rounded-lg bg-background/60 border border-border px-3 py-2"
                  data-testid={`wizard-attach-item-${a.id}`}
                >
                  <div className="h-10 w-10 rounded-md bg-secondary border border-border grid place-items-center overflow-hidden shrink-0">
                    {isImage ? (
                      <img src={a.data_url} alt={a.name} className="h-full w-full object-cover" />
                    ) : a.type === "application/pdf" ? (
                      <FileText className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                    ) : (
                      <FileImage className="h-4 w-4 text-muted-foreground" strokeWidth={1.5} />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm truncate">{a.name}</div>
                    <div className="text-xs text-muted-foreground font-mono">{kb} · {a.type || "unbekannt"}</div>
                  </div>
                  <button
                    type="button"
                    onClick={() => remove(a.id)}
                    className="h-8 w-8 rounded-full grid place-items-center text-muted-foreground hover:text-foreground hover:bg-secondary transition-colors"
                    aria-label={`„${a.name}" entfernen`}
                    data-testid={`wizard-attach-remove-${a.id}`}
                  >
                    <XIcon className="h-4 w-4" strokeWidth={1.5} />
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </div>
  );
}

function WizardCoachPanel({ data, activeStrategy, moodLabel, tradeTypeLabel, noStrategyValue }) {
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  const toNum = (v) => {
    if (v === "" || v == null) return null;
    const n = parseFloat(String(v).replace(",", "."));
    return Number.isFinite(n) ? n : null;
  };

  const analyze = async () => {
    if (!data.symbol) {
      setErr("Bitte trage mindestens ein Asset ein — der Coach braucht einen Kontext.");
      return;
    }
    setBusy(true); setErr(""); setReply("");
    try {
      const payload = {
        asset: data.symbol.trim(),
        side: data.side || "long",
        market: data.market || "",
        entry: toNum(data.entry),
        exit: toNum(data.exit),
        size: toNum(data.size),
        stop: toNum(data.stop),
        risk_pct: toNum(data.risk),
        leverage: toNum(data.leverage),
        fees: toNum(data.fees),
        strategy_id: activeStrategy?.id || null,
        strategy_name: data.strategy === noStrategyValue ? "" : (activeStrategy?.name || data.strategy || ""),
        no_strategy_reason: data.no_strategy_reason || "",
        mood_before: data.mood_before || "",
        mood_after: data.mood || "",
        reason: data.reason || "",
        reflection: data.reflection || "",
      };
      const res = await axios.post(`${API}/api/coach/analyze-trade`, payload, { withCredentials: true });
      setReply(res.data?.reply || "");
    } catch (e) {
      setErr(e?.response?.data?.detail || "Der Coach ist gerade nicht erreichbar. Versuch es gleich noch mal.");
    } finally {
      setBusy(false);
    }
  };

  // Auto-Start beim Öffnen des Schritts
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { analyze(); }, []);

  // R/R grob berechnen für Anzeige
  const rr = (() => {
    const entry = toNum(data.entry), stop = toNum(data.stop), exit = toNum(data.exit);
    if (entry == null || stop == null || exit == null) return null;
    const risk = Math.abs(entry - stop);
    if (risk === 0) return null;
    return (Math.abs(exit - entry) / risk).toFixed(2);
  })();

  return (
    <div className="rounded-xl border border-border bg-secondary/40 p-6 space-y-4" data-testid="wizard-ai">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Sparkles className="h-4 w-4 text-primary" strokeWidth={1.5} />
          <div className="overline">KI-Reflexion</div>
          {busy && (
            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground" data-testid="wizard-ai-loading">
              <Loader2 className="h-3 w-3 animate-spin" strokeWidth={1.5} /> Coach denkt nach…
            </span>
          )}
        </div>
        <Button
          type="button" variant="ghost" size="sm" className="rounded-full h-8 text-xs"
          onClick={analyze} disabled={busy}
          data-testid="wizard-ai-refresh"
        >
          <RefreshCw className={`mr-1.5 h-3.5 w-3.5 ${busy ? "animate-spin" : ""}`} strokeWidth={1.5} />
          Neu analysieren
        </Button>
      </div>

      <div className="flex flex-wrap gap-2 text-xs" data-testid="wizard-ai-facts">
        <Badge variant="secondary" className="rounded-full">
          {activeStrategy ? `Strategie: ${activeStrategy.name}` : data.strategy === noStrategyValue ? "Ohne Strategie" : "Strategie: —"}
        </Badge>
        {activeStrategy?.trade_types?.length > 0 && (
          <Badge variant="secondary" className="rounded-full">
            {activeStrategy.trade_types.map((t) => tradeTypeLabel[t] || t).join(", ")}
          </Badge>
        )}
        {rr && <Badge variant="secondary" className="rounded-full">CRV ≈ {rr} : 1</Badge>}
        {data.leverage && Number(data.leverage) > 1 && (
          <Badge variant="secondary" className="rounded-full">Hebel: {data.leverage}×</Badge>
        )}
        {data.risk && <Badge variant="secondary" className="rounded-full">Risiko: {data.risk} R</Badge>}
        {data.mood_before && <Badge variant="secondary" className="rounded-full">Vorher: {moodLabel(data.mood_before)}</Badge>}
        {data.mood && <Badge variant="secondary" className="rounded-full">Nachher: {moodLabel(data.mood)}</Badge>}
      </div>

      {err && (
        <div className="rounded-lg border border-[hsl(0_66%_50%/0.35)] bg-[hsl(0_66%_50%/0.1)] p-3 text-sm text-[hsl(0_66%_72%)]" data-testid="wizard-ai-error">
          {err}
        </div>
      )}

      {!err && (
        <div
          className="rounded-lg bg-background/60 border border-border p-4 min-h-[120px] text-sm leading-relaxed whitespace-pre-wrap text-foreground/90"
          data-testid="wizard-ai-reply"
        >
          {busy && !reply ? (
            <div className="flex items-start gap-2 text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin mt-0.5" strokeWidth={1.5} />
              <span>Coach analysiert Setup, Risiko und Muster — einen Moment.</span>
            </div>
          ) : reply ? (
            reply
          ) : (
            <span className="text-muted-foreground italic">Noch keine Analyse. Klick „Neu analysieren".</span>
          )}
        </div>
      )}

      {activeStrategy && (activeStrategy.sl_description || activeStrategy.tp_description) && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {activeStrategy.sl_description && (
            <div className="rounded-lg bg-background/50 border border-border p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Regel-Check: SL</div>
              <div className="text-xs mt-1 whitespace-pre-wrap">{activeStrategy.sl_description}</div>
            </div>
          )}
          {activeStrategy.tp_description && (
            <div className="rounded-lg bg-background/50 border border-border p-3">
              <div className="text-[10px] uppercase tracking-wider text-muted-foreground">Regel-Check: TP</div>
              <div className="text-xs mt-1 whitespace-pre-wrap">{activeStrategy.tp_description}</div>
            </div>
          )}
        </div>
      )}

      <p className="text-[10px] text-muted-foreground italic">
        KI-Reflexion ist eine unterstützende Perspektive — keine Handelsempfehlung.
      </p>
    </div>
  );
}

function ReviewCell({ label, value }) {
  return (
    <div className="rounded-lg border border-border p-3">
      <div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="mt-1 font-mono text-sm">{value}</div>
    </div>
  );
}
